import React, { useState } from 'react';
import { Check, Video as LucideIcon } from 'lucide-react';

type FallBoxType = 'fall_greens' | 'fall_essentials';
type GrowingEnvironment = 'indoor' | 'outdoor';
type SeedPackageType = 'salad_mix' | 'herb_garden' | 'greens_bag' | 'chefs_choice';

interface ProductDetails {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  slug: string;
  external_url: string | null;
  description?: string;
}

interface FeatureItem {
  text: string;
  icon?: LucideIcon;
  value?: number;
}

interface PackageCardProps {
  product: ProductDetails;
  highlighted?: boolean;
  ctaLabel: string;
  features: (string | FeatureItem)[];
  disabled?: boolean;
  selectedBoxType?: FallBoxType | null;
  onCtaClick?: (product: ProductDetails, selectedBoxType?: FallBoxType | null) => void;
  showValueSummary?: boolean;
  totalRetailValue?: number;
}

export default function PackageCard({ product, highlighted = false, ctaLabel, features, disabled = false, selectedBoxType, onCtaClick, showValueSummary = false, totalRetailValue }: PackageCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  
  // Construct the final href with box_type parameter if needed
  const finalHref = product.external_url && selectedBoxType 
    ? `${product.external_url}?box_type=${selectedBoxType}`
    : product.external_url;

  const handleCtaClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    
    if (onCtaClick) {
      e.preventDefault();
      onCtaClick(product);
    } else if (finalHref) {
      // Default behavior - open external link
      window.location.href = finalHref;
    }
  };

  return (
    <div
      className={`bg-white rounded-xl p-6 shadow-lg transition-all duration-300 relative ${disabled ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl hover:-translate-y-1'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>

      <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
      <div className="mb-4">
        {product.originalPrice && product.originalPrice > product.price ? (
          <div className="flex flex-col gap-1">
            <span className="text-lg text-gray-400 line-through">
              ${product.originalPrice}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold text-gray-900">
                ${product.price}
              </span>
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold">
                Save ${(product.originalPrice - product.price).toFixed(0)}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-3xl font-bold text-gray-900">
            ${product.price}
          </div>
        )}
      </div>

      {/* Product Features */}
      <div className="mb-6">
        <ul className="space-y-3">
          {features?.map((feature, index) => {
            const isFeatureObject = typeof feature === 'object';
            const featureText = isFeatureObject ? feature.text : feature;
            const FeatureIcon = isFeatureObject && feature.icon ? feature.icon : Check;
            const featureValue = isFeatureObject ? feature.value : undefined;

            return (
              <li key={index} className="flex items-start gap-2 text-sm">
                <FeatureIcon className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 flex items-start justify-between gap-2">
                  <span className="text-gray-700">{featureText}</span>
                  {featureValue && (
                    <span className="text-gray-500 font-medium whitespace-nowrap">
                      ${featureValue} value
                    </span>
                  )}
                </div>
              </li>
            );
          }) || (
            <li className="text-sm text-gray-600">{product.description}</li>
          )}
        </ul>
      </div>

      {/* Value Summary */}
      {showValueSummary && totalRetailValue && (
        <div className="mb-6 p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 font-medium">Total Retail Value:</span>
              <span className="text-gray-900 font-bold text-lg">${totalRetailValue}</span>
            </div>
            <div className="border-t border-green-200 pt-2">
              <div className="flex items-center justify-between text-base font-bold">
                <span className="text-green-700">Your Price:</span>
                <span className="text-green-700 text-2xl">FREE</span>
              </div>
              <p className="text-xs text-gray-600 mt-1 text-center">
                Just Pay $9.99 Shipping
              </p>
            </div>
          </div>
        </div>
      )}
      
      <button
        onClick={handleCtaClick}
        disabled={disabled || (!product.external_url && !onCtaClick)}
        data-tier={product.name}
        className={`block w-full text-center py-3 px-4 rounded-lg font-medium transition-all duration-300 ${
          disabled || (!product.external_url && !onCtaClick)
            ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
            : 'bg-primary-500 text-white hover:bg-primary-600'
        }`}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
      >
        {!product.external_url && !onCtaClick ? 'Currently Unavailable' : ctaLabel}
      </button>
    </div>
  );
}